import { RiLoader4Line } from 'react-icons/ri';

const SavedLoginsModal = ({
  open = false,
  isWebMode = false,
  canUseVault = false,
  savedLoginsTab = 'local',
  setSavedLoginsTab,
  localLogins = [],
  vaultLogins = [],
  savedLogins = [],
  vaultStatus,
  vaultPassword,
  setVaultPassword,
  vaultUnlocking = false,
  vaultError = '',
  onVaultUnlock,
  connectingLoginName = null,
  onConnectLogin,
  onDeleteLogin,
  onClose
}) => {
  if (!open) return null;

  const displayedLogins =
    isWebMode ?
      savedLogins :
      canUseVault && savedLoginsTab === 'vault' ?
        vaultLogins :
        localLogins;

  const webVaultLocked =
    isWebMode &&
    canUseVault &&
    vaultStatus?.hasMaster &&
    !vaultStatus?.unlocked &&
    vaultLogins.length > 0;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4 backdrop-blur">
      <div className="glass-panel w-full max-w-3xl rounded-2xl p-5 flex flex-col gap-4 max-h-[80vh]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold text-textMuted">Saved logins</p>
            <h3 className="text-lg font-semibold text-white m-0">Choose a server</h3>
          </div>
          <button type="button" className="ghost-button" onClick={onClose}>
            Close
          </button>
        </div>

        {!isWebMode &&
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 bg-inset/60 rounded-lg border border-border p-1">
              <button
                type="button"
                className={`tab-button ${savedLoginsTab === 'local' ? 'tab-button-active' : ''}`}
                onClick={() => setSavedLoginsTab('local')}>
                Local ({localLogins.length})
              </button>
              {canUseVault ?
                <button
                  type="button"
                  className={`tab-button ${savedLoginsTab === 'vault' ? 'tab-button-active' : ''}`}
                  onClick={() => setSavedLoginsTab('vault')}>
                  Vault ({vaultLogins.length})
                </button> :
                null}
            </div>
            {canUseVault && savedLoginsTab === 'vault' && !vaultStatus.unlocked && vaultStatus.hasMaster ?
              <span className="text-xs text-textMuted">Unlock required to connect.</span> :
              null}
          </div>}

        {!isWebMode && canUseVault && savedLoginsTab === 'vault' ?
          <div className="rounded-lg border border-border bg-inset/60 p-3">
            {vaultStatus.unlocked ?
              <div className="text-sm text-textMuted">Vault unlocked. Pick a login to connect.</div> :
              vaultStatus.hasMaster ?
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      className="flex-1 rounded-lg border border-border bg-inset/70 px-3 py-2 text-sm text-white placeholder:text-textMuted focus:border-accent focus:outline-none"
                      placeholder="Vault password"
                      type="password"
                      value={vaultPassword}
                      onChange={(e) => setVaultPassword(e.target.value)} />
                    <button
                      type="button"
                      className="pill-button px-4 py-2"
                      onClick={onVaultUnlock}
                      disabled={vaultUnlocking || !vaultPassword}>
                      {vaultUnlocking ? 'Unlocking...' : 'Unlock'}
                    </button>
                  </div>
                  {vaultError ? <p className="mt-2 text-xs text-red-300">{vaultError}</p> : null}
                </> :
                <div className="text-sm text-textMuted">Vault not configured yet.</div>}
          </div> :
          null}

        {webVaultLocked ?
          <div className="rounded-lg border border-border bg-inset/60 p-3">
            <div className="text-sm text-textMuted">
              Some saved logins are encrypted. Unlock with your saved-logins key to connect to them.
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input
                className="flex-1 rounded-lg border border-border bg-inset/70 px-3 py-2 text-sm text-white placeholder:text-textMuted focus:border-accent focus:outline-none"
                placeholder="Saved logins key"
                type="password"
                value={vaultPassword}
                onChange={(e) => setVaultPassword(e.target.value)} />
              <button
                type="button"
                className="pill-button px-4 py-2"
                onClick={onVaultUnlock}
                disabled={vaultUnlocking || !vaultPassword}>
                {vaultUnlocking ? 'Unlocking...' : 'Unlock'}
              </button>
            </div>
            {vaultError ? <p className="mt-2 text-xs text-red-300">{vaultError}</p> : null}
          </div> :
          null}

        {isWebMode ?
          <p className="text-xs text-textMuted">
            {webVaultLocked ?
              'Unencrypted logins can connect immediately. Encrypted logins need your saved-logins key first.' :
              'Logins are saved in your browser storage. Click to connect.'}
          </p> :
          null}

        <div className="flex-1 overflow-y-auto space-y-2">
          {displayedLogins.length === 0 ?
            <div className="text-sm text-textMuted">No saved logins yet.</div> :
            displayedLogins.map((entry) => {
              const isConnectingThis = connectingLoginName === entry.name;
              const requiresUnlock =
                isWebMode &&
                entry.encrypted &&
                canUseVault &&
                vaultStatus?.hasMaster &&
                !vaultStatus?.unlocked;

              return (
                <div
                  key={entry.name}
                  role="button"
                  tabIndex={requiresUnlock ? -1 : 0}
                  aria-disabled={requiresUnlock}
                  onClick={() => {
                    if (requiresUnlock) return;
                    onConnectLogin(entry);
                  }}
                  onKeyDown={(event) => {
                    if (requiresUnlock) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onConnectLogin(entry);
                    }
                  }}
                  className={`flex items-center justify-between gap-3 rounded-xl border bg-inset/60 px-4 py-3 text-left transition-colors ${
                    isConnectingThis ?
                      'border-accent/60 bg-white/5' :
                      requiresUnlock ?
                        'border-border/60 opacity-70 cursor-not-allowed' :
                        'border-border hover:border-accent/60 hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-accent/60 cursor-pointer'
                  }`}>
                  <div className="min-w-0 flex items-center gap-3">
                    {isConnectingThis ? <RiLoader4Line className="shrink-0 text-accent animate-spin" /> : null}
                    <div className="min-w-0">
                      <div className="flex min-w-0 items-center gap-2">
                        <p className="text-sm font-semibold text-white truncate">{entry.name}</p>
                        {entry.encrypted ?
                          <span className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-wide text-textMuted">
                            Encrypted
                          </span> :
                          null}
                      </div>
                      <p className="text-xs text-textMuted truncate">
                        {isConnectingThis ?
                          'Connecting...' :
                          requiresUnlock ?
                            'Unlock required before connecting.' :
                            (entry.description || 'No description')}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="ghost-button px-3 py-1"
                    onClick={(event) => {
                      event.stopPropagation();
                      onDeleteLogin(entry.name);
                    }}>
                    Delete
                  </button>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default SavedLoginsModal;
